import type {
  LocaleOption,
  PublishJobDto,
  PublishJobProgress,
  PublishJobStepOutcome,
} from '@course-authoring/shared-types';

export interface PublishJobLanguageOutcome {
  locale: string;
  label: string;
  tags: string[];
  failed: boolean;
  /** At least one outcome was recorded for this target locale. */
  done: boolean;
}

export interface PublishJobStepSummary {
  summary: string;
  progress: PublishJobProgress | null;
  languages: PublishJobLanguageOutcome[];
  noVoiceSentence: string | null;
  otherSkipSentences: string[];
}

/** Count each target locale once after any outcome has been recorded for it. */
export function derivePublishJobProgress(
  step: string,
  targetLocales: string[] | undefined,
  outcomes: PublishJobStepOutcome[],
): PublishJobProgress | null {
  const targets = [...new Set(targetLocales ?? [])];
  if (targets.length === 0) return null;

  const targetSet = new Set(targets);
  const processed = new Set(outcomes.flatMap((outcome) => (
    outcome.step === step && outcome.locale && targetSet.has(outcome.locale) ? [outcome.locale] : []
  )));
  return { completed: processed.size, total: targets.length };
}

/** Derive current-step progress from a full job read without interpreting handler-owned step keys. */
export function deriveCurrentPublishJobProgress(
  job: Pick<PublishJobDto, 'currentStep' | 'stepResults' | 'result'>,
): PublishJobProgress | null {
  const step = job.currentStep;
  if (!step) return null;
  return derivePublishJobProgress(step, job.result.targets?.[step], job.stepResults);
}

/** Present field-level outcomes as distinct language states and language-based summaries. */
export function summarizePublishJobLanguageStep(
  step: 'translation' | 'narration',
  outcomes: PublishJobStepOutcome[],
  languageOptions: LocaleOption[],
  targetLocales?: string[],
): PublishJobStepSummary {
  const labels = new Map(languageOptions.map(({ locale, label }) => [locale, label]));
  const languageName = (locale: string) => labels.get(locale) ?? locale;
  const relevant = outcomes.filter((outcome) => outcome.step === step);
  const targets = [...new Set(targetLocales ?? [])];
  const hasTargets = targets.length > 0;
  const progress = derivePublishJobProgress(step, targets, relevant);
  const localeSet = (items: PublishJobStepOutcome[]) => new Set(items.flatMap((item) => item.locale ? [item.locale] : []));
  const failed = relevant.filter((outcome) => outcome.status === 'failed');
  const failedLocales = localeSet(failed);
  const failedWithoutLocaleCount = failed.filter((outcome) => !outcome.locale).length;
  const generatedOrReused = relevant.filter((outcome) => outcome.status === 'generated' || outcome.status === 'reused');
  const targetSet = hasTargets ? new Set(targets) : null;
  const inTargetScope = (outcome: PublishJobStepOutcome) => !targetSet
    || Boolean(outcome.locale && targetSet.has(outcome.locale));
  const preparedLocales = localeSet(generatedOrReused.filter(inTargetScope));
  const reusedLocales = localeSet(relevant.filter((outcome) => outcome.status === 'reused' && inTargetScope(outcome)));
  const failedCount = failedLocales.size + failedWithoutLocaleCount;

  const summary = failedCount
    ? failedLocales.size
      ? `${failedLocales.size} ${failedLocales.size === 1 ? 'language failed' : 'languages failed'}${failedWithoutLocaleCount
        ? ` · ${failedWithoutLocaleCount} ${failedWithoutLocaleCount === 1 ? 'step error' : 'step errors'}`
        : ''}`
      : `${step === 'translation' ? 'Translation' : 'Audio narration'} failed before a language was recorded`
    : step === 'translation'
      ? preparedLocales.size || hasTargets
        ? `${preparedLocales.size} ${preparedLocales.size === 1 ? 'language translated' : 'languages translated'}${reusedLocales.size ? ` · ${reusedLocales.size} kept` : ''}`
        : 'No outcomes recorded yet'
      : hasTargets
        ? `${preparedLocales.size} of ${targets.length} languages narrated`
        : preparedLocales.size || relevant.length
          ? `${preparedLocales.size} ${preparedLocales.size === 1 ? 'language narrated' : 'languages narrated'}`
          : 'No outcomes recorded yet';

  const visible = relevant.filter((outcome) => outcome.status !== 'skipped' || outcome.code === 'VOICE_UNSUPPORTED');
  const locales = hasTargets ? targets : [...localeSet(visible)];
  const languages = locales.map((locale) => {
    const localeOutcomes = relevant.filter((outcome) => outcome.locale === locale);
    const tags = [...new Set(localeOutcomes.flatMap((outcome) => {
      if (outcome.status === 'failed') return ['Failed'];
      if (outcome.status === 'reused') return ['Kept'];
      if (outcome.code === 'VOICE_UNSUPPORTED') return ['No voice'];
      return [];
    }))];
    return {
      locale,
      label: languageName(locale),
      tags,
      failed: localeOutcomes.some((outcome) => outcome.status === 'failed'),
      done: localeOutcomes.length > 0,
    };
  });

  const noVoiceLocales = [...localeSet(relevant.filter((outcome) => outcome.status === 'skipped' && outcome.code === 'VOICE_UNSUPPORTED'))];
  const noVoiceSentence = noVoiceLocales.length
    ? `No narration voice is available for ${noVoiceLocales.map(languageName).join(', ')}. Learners see text only.`
    : null;
  const otherSkips = relevant.filter((outcome) => outcome.status === 'skipped'
    && outcome.code !== 'NARRATION_TEXT_EMPTY' && outcome.code !== 'VOICE_UNSUPPORTED');
  const skipGroups = new Map<string, { message: string; locales: Set<string> }>();
  for (const outcome of otherSkips) {
    const message = outcome.message ?? (step === 'translation' ? 'Some translations were skipped.' : 'Some narration was skipped.');
    const key = `${outcome.code ?? 'OTHER'}|${message}`;
    const group = skipGroups.get(key) ?? { message, locales: new Set<string>() };
    if (outcome.locale) group.locales.add(languageName(outcome.locale));
    skipGroups.set(key, group);
  }
  const otherSkipSentences = [...skipGroups.values()].map(({ message, locales: skippedLocales }) => {
    if (!skippedLocales.size) return message;
    return `${message} Languages: ${[...skippedLocales].join(', ')}.`;
  });

  return { summary, progress, languages, noVoiceSentence, otherSkipSentences };
}

export function isActivePublishJob(job: { status: string } | null | undefined): boolean {
  return job?.status === 'queued' || job?.status === 'running';
}

export function summarizePublishJobStep(
  step: string,
  outcomes: PublishJobStepOutcome[],
  languageOptions: LocaleOption[],
  targets?: string[],
): PublishJobStepSummary {
  if (step === 'translation' || step === 'narration') {
    return summarizePublishJobLanguageStep(step, outcomes, languageOptions, targets);
  }
  const relevant = outcomes.filter((outcome) => outcome.step === step);
  const count = (status: PublishJobStepOutcome['status']) => relevant.filter((outcome) => outcome.status === status).length;
  const parts = [['generated', 'completed'], ['reused', 'kept'], ['skipped', 'skipped'], ['failed', 'failed']] as const;
  return {
    summary: relevant.length ? parts.flatMap(([status, label]) => count(status) ? [`${count(status)} ${label}`] : []).join(' · ') : 'No outcomes recorded yet',
    progress: null, languages: [], noVoiceSentence: null, otherSkipSentences: [],
  };
}
